import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Editor, NgxEditorModule, Toolbar, toHTML } from 'ngx-editor';

import { ContasPagarGridItemStruct } from '../../../../../shared/services/structs/financas/contas-pagar-grid-item.struct';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';
import { PagamentoContasPagarParcelaService } from '../../../../../shared/services/apis/financas/pagamento-contas-pagar-parcela.service';
import { PagamentoContasPagarRequest, PagamentoParcelaItemRequest } from '../../../../../shared/services/requests/financas/pagamento-contas-pagar.request';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { MaskService } from '../../../../../shared/services/mask.service';
import { NgxCurrencyDirective } from 'ngx-currency';
import { PrepararPagamentoParcelaItemStruct } from '../../../../../shared/services/responses/financas/preparar-pagamento-contas-pagar.response';
import { UtilService } from '../../../../../shared/services/util.service';
import { FutureDateValidator } from '../../../../../shared/validators/future-date.validator';

export interface PagarContasDialogData {
    parcelas: ContasPagarGridItemStruct[];
    listaCategorias: any[];
    listaContasFinanceiras: any[];
}

@Component({
    selector: 'app-pagar-contas-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatProgressSpinnerModule,
        NgxEditorModule,
        SelectPesquisavelComponent,
        AbntPipe,
        NgxCurrencyDirective
    ],
    templateUrl: './pagar-contas-dialog.component.html',
    styleUrls: ['./pagar-contas-dialog.component.css']
})
export class PagarContasDialogComponent implements OnInit, OnDestroy {
    form: FormGroup;
    editor: Editor;
    toolbar: Toolbar = [
        ['bold', 'italic'],
        ['underline', 'strike'],
        ['ordered_list', 'bullet_list'],
        [{ heading: ['h1', 'h2', 'h3'] }],
        ['text_color', 'background_color'],
        ['align_left', 'align_center', 'align_right', 'align_justify'],
    ];

    currencyMask: any;
    isSaving = false;
    isLoading = true;
    maxDataPagamento = new Date();

    totalRecebido = 0;

    constructor(
        public dialogRef: MatDialogRef<PagarContasDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: PagarContasDialogData,
        private fb: FormBuilder,
        private pagamentoService: PagamentoContasPagarParcelaService,
        private alertService: AlertService,
        private maskService: MaskService
    ) {
        this.editor = new Editor();
        this.currencyMask = { ...this.maskService.getCurrencyMask(), allowNegative: false };

        this.form = this.fb.group({
            idContaFinanceira: [null, Validators.required],
            dataPagamento: [new Date(), [Validators.required, FutureDateValidator.notFuture()]],
            observacoesHtml: [null],
            parcelas: this.fb.array([])
        });
    }

    ngOnInit(): void {
        this.prepararPagamento();

        this.parcelasFormArray.valueChanges.subscribe(() => {
            this.calcularTotal();
        });
    }

    ngOnDestroy(): void {
        this.editor.destroy();
    }

    get parcelasFormArray(): FormArray {
        return this.form.get('parcelas') as FormArray;
    }

    private prepararPagamento(): void {
        const req = {
            idsParcelas: this.data.parcelas.map(p => p.idContasPagarParcela)
        };

        this.pagamentoService.preparar(req).subscribe({
            next: (res) => {
                this.isLoading = false;
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription, AlertType.error);
                    this.fechar();
                    return;
                }
                this.popularParcelasPreparadas(res.parcelas);
                this.calcularTotal();
            },
            error: (err) => {
                this.isLoading = false;
                this.alertService.show('Erro', err.message || 'Erro ao carregar dados.', AlertType.error);
                this.fechar();
            }
        });
    }

    private popularParcelasPreparadas(parcelas: PrepararPagamentoParcelaItemStruct[]): void {
        parcelas.forEach(item => {
            const jurosPrevisto = UtilService.arredondarAbnt(item.valorJurosPrevisto || 0, this.currencyMask.precision);
            const multaPrevista = UtilService.arredondarAbnt(item.valorMultaPrevisto || 0, this.currencyMask.precision);
            const acrescimoPrevisto = item.valorAcrescimoPrevisto || 0;
            const descontoPrevisto = item.valorDescontoPrevisto || 0;
            const total = UtilService.arredondarAbnt(item.valorPrincipalEmAberto + jurosPrevisto + multaPrevista + acrescimoPrevisto - descontoPrevisto, this.currencyMask.precision);
            let encargosFixos = UtilService.arredondarAbnt(jurosPrevisto + multaPrevista + acrescimoPrevisto - descontoPrevisto, this.currencyMask.precision);
            if (encargosFixos < 0) encargosFixos = 0;

            const formObj = this.fb.group({
                idContasPagarParcela: [item.idContasPagarParcela],
                idCategoriaFinanceira: [item.idCategoriaFinanceira, Validators.required],
                numeroDocumento: [item.numeroDocumento],
                fornecedor: [item.fornecedor],
                descricao: [item.descricao],
                vencimento: [item.vencimento],
                valorOriginal: [item.valorOriginal],
                valorAPagar: [item.valorPrincipalEmAberto],
                valorMulta: [multaPrevista || null],
                valorJuros: [jurosPrevisto || null],
                valorDesconto: [item.valorDescontoPrevisto || null],
                valorAcrescimo: [item.valorAcrescimoPrevisto || null],
                valorPago: [total > 0 ? total : 0, [Validators.required, Validators.min(encargosFixos)]],
                isValido: [true]
            });

            formObj.get('valorPago')?.valueChanges.subscribe(() => {
                this.calcularTotal();
            });

            this.parcelasFormArray.push(formObj);
        });
    }

    private calcularTotal(): void {
        let total = 0;
        const parcelas = this.parcelasFormArray.getRawValue();
        parcelas.forEach(p => {
            total += Number(p.valorPago || 0);
        });
        this.totalRecebido = UtilService.arredondarAbnt(total, this.currencyMask.precision);
    }

    calcularTotalCard(index: number) {
        const item = this.parcelasFormArray.at(index);
        const aPagar = item.get('valorAPagar')?.value || 0;
        const juros = item.get('valorJuros')?.value || 0;
        const multa = item.get('valorMulta')?.value || 0;
        const desc = item.get('valorDesconto')?.value || 0;
        const acr = item.get('valorAcrescimo')?.value || 0;

        let pago = aPagar + juros + multa + acr - desc;
        pago = UtilService.arredondarAbnt(pago, this.currencyMask.precision);

        let encargosMinimos = UtilService.arredondarAbnt(juros + multa + acr - desc, this.currencyMask.precision);
        if (encargosMinimos < 0) encargosMinimos = 0;

        // Dynamically enforce that the payment cannot be less than the accumulated interest and taxes
        item.get('valorPago')?.setValidators([Validators.required, Validators.min(encargosMinimos)]);
        item.get('valorPago')?.setValue(pago >= 0 ? pago : 0, { emitEvent: false });
        item.get('valorPago')?.updateValueAndValidity({ emitEvent: false });

        this.calcularTotal();
    }

    salvar(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.alertService.show('Atenção', 'Preencha todos os campos obrigatórios corretamente.', AlertType.warning);
            return;
        }

        const parcelasEnviadas = this.parcelasFormArray.getRawValue();
        const algumaZerada = parcelasEnviadas.some((p: any) => p.valorPago <= 0);
        if (algumaZerada) {
            this.alertService.show('Atenção', 'O valor pago deve ser maior que zero em todos os documentos.', AlertType.warning);
            return;
        }

        this.isSaving = true;
        const formValue = this.form.getRawValue();
        let parsedHtml = '';
        if (formValue.observacoesHtml) {
            parsedHtml = typeof formValue.observacoesHtml === 'object' ? toHTML(formValue.observacoesHtml) : formValue.observacoesHtml;
        }

        const request: PagamentoContasPagarRequest = {
            idContaFinanceira: formValue.idContaFinanceira,
            dataPagamento: formValue.dataPagamento,
            observacoesHtml: parsedHtml,
            parcelas: formValue.parcelas.map((val: any) => {
                const itemReq: PagamentoParcelaItemRequest = {
                    idContasPagarParcela: val.idContasPagarParcela,
                    idCategoriaFinanceira: val.idCategoriaFinanceira,
                    valorPago: val.valorPago,
                    valorJuros: val.valorJuros,
                    valorMulta: val.valorMulta,
                    valorDesconto: val.valorDesconto,
                    valorAcrescimo: val.valorAcrescimo
                };
                return itemReq;
            })
        };

        this.pagamentoService.post(request).subscribe({
            next: (res: any) => {
                this.isSaving = false;
                if (res.isError) {
                    this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
                    return;
                }
                this.alertService.show('Sucesso', 'Pagamento realizado com sucesso!', AlertType.success);
                this.dialogRef.close(true);
            },
            error: (err: any) => {
                this.isSaving = false;
                this.alertService.show('Erro', err.message || 'Erro ao processar pagamento.', AlertType.error);
            }
        });
    }

    fechar(): void {
        this.dialogRef.close();
    }
}
